import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";

import {
  FORGOT_PASS_PATH,
  RESET_PASS_PATH,
  SIGNIN_PATH,
  SIGNUP_PATH,
  SIGNUP_VERIFY_EMAIL_PATH,
} from "features/Auth/routes";
import {
  collapsedSidebarSelect,
  hasBannerSelect,
  userDetailSelect,
} from "Layout/slice/selector";

import { logoBetaAIDark, SidebarUpgradeLogo } from "assets/icons";
import { AppMenu } from "./AppMenu";
import { contains, isLoggedIn } from "utils";
import { PLANS_PATH } from "features/Plans/routes";
import { RecommendCardContainer, SidebarContainer } from "./Sidebar.styles";
import { MAIN_ACCOUNT_PATH } from "features/Account/routes";
import { HOME_PATH } from "features/Dashboard/routes";
import { VERIFY_PHONE_PATH, WELCOME_PATH } from "features/Auth/routes";
import { themeState } from "Layout/slice/selector";
import { ProfileMenu } from "./ProfileMenu";
import { togglePurchasePlanModal } from "Layout/slice/index";
import { HELP_CENTER_V2_PATH } from "features/HelpCenter/routes";
import {
  IconBackArrow,
  surmountLogo,
  SidebarSurmountLogo,
} from "assets/icons/index";
import { X } from "lucide-react";
import { useNavigation } from "utils/navigate";
import { setCollapsedSidebar } from "Layout/slice";

export const Sidebar = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const userDetails = useSelector(userDetailSelect);
  const { navigateBack } = useNavigation();

  const theme = useSelector(themeState);
  const hasBanner = useSelector(hasBannerSelect);
  const collapsed = useSelector(collapsedSidebarSelect);

  const [active, setActive] = useState("");

  const planId = userDetails?.surmount_plan?.id || "";
  const isPlanLoaded = !!userDetails?.surmount_plan;
  // Plan tiers drive the sidebar card: Free shows the "Upgrade" card,
  // Core/Plus show the "Manage" card, and Pro hides the card entirely.
  const isProOrPremium = planId.includes("pro") || planId.includes("premium");
  const isCoreOrPlus = planId.includes("core") || planId.includes("plus");

  useEffect(() => {
    setActive(pathname);
  }, [pathname]);

  const hideSidebar =
    !isLoggedIn() ||
    contains(pathname, SIGNUP_PATH) ||
    contains(pathname, SIGNIN_PATH) ||
    contains(pathname, FORGOT_PASS_PATH) ||
    contains(pathname, SIGNUP_VERIFY_EMAIL_PATH) ||
    contains(pathname, RESET_PASS_PATH) ||
    contains(pathname, WELCOME_PATH) ||
    contains(pathname, PLANS_PATH) ||
    contains(pathname, VERIFY_PHONE_PATH) ||
    contains(pathname, MAIN_ACCOUNT_PATH);

  const getLogo = () => {
    let logo = SidebarSurmountLogo;
    if (theme === "dark") {
      logo = logoBetaAIDark;
    }
    return logo;
  };

  const handleUpgradePlan = () => {
    dispatch(togglePurchasePlanModal(true));
  };

  const handleBackButton = () => {
    navigateBack();
  };

  if (hideSidebar) return null;

  return (
    <SidebarContainer
      hasBanner={hasBanner}
      className={classNames({
        collapsed: collapsed,
      })}
    >
      <div className="sidebar">
        <div className="sidebar-inner">
          <div
            className={classNames("sidebar-layout", {
              "sidebar-account-layout": contains(pathname, "account"),
            })}
          >
            <div className="sidebar-logo-header">
              {contains(pathname, "account") ? (
                <>
                  <div className="mobile-view">
                    <div className="account-header">
                      <Link to={HOME_PATH} className="link-logo">
                        <img
                          alt="logo"
                          src={surmountLogo}
                          className="main-logo account-logo"
                        />
                      </Link>
                      <IconBackArrow
                        variant="secondary"
                        size="small"
                        className="btn-back-secondary-page"
                        onClick={handleBackButton}
                      />
                      <span className="item-content">Account settings</span>
                    </div>
                  </div>
                  <div className="desktop-view">
                    <Link to={HOME_PATH} className="link-logo">
                      <img alt="logo" src={getLogo()} className="main-logo" />
                    </Link>
                  </div>
                </>
              ) : (
                <Link to={HOME_PATH} className="link-logo">
                  <img
                    alt="logo"
                    src={getLogo()}
                    className="main-logo"
                    height={38}
                    width={140}
                  />
                </Link>
              )}
              <button
                className="sidebar-close-btn"
                aria-label="Close sidebar"
                onClick={() => dispatch(setCollapsedSidebar(false))}
              >
                <X size={20} strokeWidth={2.5} />
              </button>
            </div>

            <div className="sidebar-content">
              <nav className="menu">
                {contains(pathname, MAIN_ACCOUNT_PATH) ||
                contains(pathname, HELP_CENTER_V2_PATH) ? (
                  <ProfileMenu active={active} />
                ) : (
                  <AppMenu active={active} />
                )}
              </nav>
            </div>
            {isPlanLoaded && !isProOrPremium && (
              <RecommendCardContainer
                className={classNames({
                  "account-recommend-card": contains(pathname, "account"),
                })}
                onClick={handleUpgradePlan}
              >
                <div className="upgrade-content">
                  <div className="upgrade-text">
                    <p className="upgrade-title">
                      {isCoreOrPlus
                        ? "Manage Surmount Premium"
                        : "Upgrade to Surmount Premium"}
                    </p>
                    <p className="upgrade-description">
                      {isCoreOrPlus
                        ? "Manage your premium account, and unlock additional investment features and further customization"
                        : "Access automated strategies, connect broker accounts, and enjoy live trading!"}
                    </p>
                  </div>
                  <div className="upgrade-visual">
                    <button className="arrow-button">&#8599;</button>
                    <div className="placeholder-image">
                      <img src={SidebarUpgradeLogo} height={75} width={75} />
                    </div>
                  </div>
                </div>
              </RecommendCardContainer>
            )}
          </div>
        </div>
      </div>
    </SidebarContainer>
  );
};
